import { useHotkeys, useLocalStorage } from '@mantine/hooks';
import type { QueryClient } from '@tanstack/react-query';
import { createRootRouteWithContext, useRouterState } from '@tanstack/react-router';
import { lazy } from 'react';
import { useScan } from 'react-scan';

import { useAppleChannelNavMenu } from '@apple/channel/hooks/useAppleChannelNavMenu';
import { AppleLayout } from '@apple/ui/layouts/AppleLayout';
import { MaintenancePage } from '@apple/ui/shell/pages/MaintenancePage';
import { getEnv } from '@apple/utils/config/env';

import type { AuthContext } from '@apple/features/auth-channel/contexts/auth-context';
import { CartMenu } from '@apple/features/cart-channel/components/CartMenu';
import type { CurrentUserLanguage } from '@apple/features/user/hooks/useCurrentUserLanguage';

export type RootRouteContext = {
	queryClient: QueryClient;
	auth: AuthContext;
	lang: CurrentUserLanguage;
};

export const Route = createRootRouteWithContext<RootRouteContext>()({
	wrapInSuspense: true,
	component: RootRoute,
});

const env = getEnv();
const devtoolsEnabled = env.DEV && env.APPLE_DISABLE_DEV_TOOLS !== true;

function RootRoute() {
	const { auth } = Route.useRouteContext();
	const [devtoolsVisible, setDevtoolsVisible] = useLocalStorage({
		key: 'devtools-visible',
		defaultValue: devtoolsEnabled,
		getInitialValueInEffect: false,
	});
	useHotkeys([['alt+x', () => devtoolsEnabled && setDevtoolsVisible(!devtoolsVisible)]]);
	useScan({
		enabled: devtoolsEnabled,
		showToolbar: devtoolsVisible,
	});

	return (
		<>
			{auth.error ? <MaintenancePage /> : <Layout />}
			{devtoolsVisible && (
				<>
					<TanStackRouterDevtools />
					<ReactQueryDevtools />
				</>
			)}
		</>
	);
}

function Layout() {
	const { menuItems } = useAppleChannelNavMenu();
	const isLoading = useRouterState({ select: s => s.status === 'pending' });
	return <AppleLayout menuItems={menuItems} loading={isLoading} cartMenu={<CartMenu />} />;
}

const TanStackRouterDevtools = import.meta.env.PROD
	? () => null // Render nothing in production
	: lazy(() =>
			// Lazy load in development
			import('@tanstack/react-router-devtools').then(res => ({
				default: res.TanStackRouterDevtools,
			})),
		);

const ReactQueryDevtools = import.meta.env.PROD
	? () => null // Render nothing in production
	: lazy(() =>
			// Lazy load in development
			import('@tanstack/react-query-devtools').then(res => ({
				default: res.ReactQueryDevtools,
			})),
		);
